import { Injectable, OnDestroy, inject } from "@angular/core";
import {
    BehaviorSubject,
    Observable,
    Subject,
    iif,
    of
} from "rxjs";
import { map, switchMap, takeUntil } from "rxjs/operators";
import { SpatialService } from "~app/common/components/map/services/spatial.service";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { CommandManager } from "~app/common/services/cqs/commandManager";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { CreateAvverkningsanmalanCommand } from "../commands/atgard-avverkningsanmalan/CreateAvverkningsanmalanCommand";
import { UpdateAvverkningsanmalanCommand } from "../commands/atgard-avverkningsanmalan/UpdateAvverkningsanmalanCommand";
import { UpdateAvverkningsanmalanRaskStatusCommand } from "../commands/atgard-avverkningsanmalan/UpdateAvverkningsanmalanRaskStatusCommand";
import { Avverkningsanmalan, AvverkningsanmalanRaskStatus, VKAvverkningsanmalanDTO } from "../models/avverkningsanmalan-models";
import { AtgardMediatorService } from "./atgard-mediator.service";
import { AvtalService } from "./avtal.service";
import { AvverkningsanmalanService } from "./avverkningsanmalan.service";
import { KontraktCommandService } from "./kontrakt-command.service";
import { KontraktMediatorService } from "./kontrakt-mediator.service";

@Injectable()
export class AvverkningsanmalanMediatorService implements OnDestroy {
    private readonly raskModalService = inject(RaskModalService);
    private readonly kontraktCommandService = inject(KontraktCommandService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly commandManager = inject(CommandManager);
    private readonly avtalService = inject(AvtalService);
    private readonly atgardMediatorService = inject(AtgardMediatorService);
    private readonly avverkningsanmalanService = inject(AvverkningsanmalanService);
    private readonly spatialService = inject(SpatialService);

    private readonly avverkningsanmalanSrc = new BehaviorSubject<Avverkningsanmalan>(null);
    private readonly avverkningsanmalanUpdatedSrc = new BehaviorSubject<Avverkningsanmalan>(null);

    get avverkningsanmalan$(): Observable<Avverkningsanmalan> {
        return this.avverkningsanmalanSrc.asObservable();
    }

    get avverkningsanmalanUpdated$(): Observable<Avverkningsanmalan> {
        return this.avverkningsanmalanUpdatedSrc.asObservable();
    }

    get avverkningsanmalan(): Avverkningsanmalan {
        return this.avverkningsanmalanSrc.value;
    }

    private readonly destroy$ = new Subject<void>();

    constructor() {
        this.atgardMediatorService.onDeleteCallbacks.push(() => {
            if (this.avverkningsanmalanService.hasActiveAnmalanWebb(this.atgardMediatorService.atgard, this.avverkningsanmalan)) {
                this.raskModalService.info(new ModalSettings("Observera", "Det finns en pågående anmälan i Anmälan Webb. Anmälan måste återkallas innan åtgärden kan tas bort."));
                return false;
            }
            return true;
        });
    }
    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    init() {
        this.atgardMediatorService.atgard$.pipe(
            switchMap(atgard => iif(
                () => atgard.id > 0,
                this.avtalService.getAvverkningsanmalanForAtgard(atgard.id),
                of(null))
            ),
            map(avverkningsanmalan => {
                if (ObjectUtils.isNullOrUndefined(avverkningsanmalan)) {
                    this.create();
                }
                return new Avverkningsanmalan(avverkningsanmalan);
            }),
            takeUntil(this.destroy$)
        ).subscribe(avverkningsanmalan => {
            this.avverkningsanmalanSrc.next(avverkningsanmalan);
        });
    }

    create() {
        const command = new CreateAvverkningsanmalanCommand(this.kontraktMediatorService.kontrakt.guid, this.atgardMediatorService.atgard.guid);
        this.kontraktCommandService.update(command);
    }

    updateCommand(atgardGuid: string, avverkningsanmalan: VKAvverkningsanmalanDTO) {
        const command = new UpdateAvverkningsanmalanCommand(this.kontraktMediatorService.kontrakt.guid, atgardGuid, avverkningsanmalan);
        this.kontraktCommandService.update(command);
        this.avverkningsanmalanUpdatedSrc.next(new Avverkningsanmalan(avverkningsanmalan));
    }

    checkSetAvverkningsanmalanRaskStatusCommand(raskStatus: number, avverkningsanmalan: Avverkningsanmalan, atgardGuid: string): void {
        const cmd = new UpdateAvverkningsanmalanRaskStatusCommand(this.kontraktMediatorService.kontrakt.guid, atgardGuid, raskStatus);
        if (raskStatus === avverkningsanmalan.raskStatus) {
            // om befintlig status är samma som persisterad så ska vi inte skicka något kommando
            this.commandManager.remove(cmd);
            return;
        }
        this.commandManager.updateCommand(cmd);
    }

    getRaskStatusForAvverkningsanmalan(
        avverkningsanmalanGeometri: string,
        persistedAtgardsgeometri: string,
        atgardsgeometri: string,
        avverkningsanmalan: Avverkningsanmalan): number {

        if (!ObjectUtils.hasValue(atgardsgeometri)) {
            return AvverkningsanmalanRaskStatus.okand;
        }

        if (!avverkningsanmalan.isAvverkningsanmalanGjord) {
            if (!ObjectUtils.hasValue(persistedAtgardsgeometri)) {
                return AvverkningsanmalanRaskStatus.okand;
            }

            const persistedArea = this.spatialService.getAreaWkt(persistedAtgardsgeometri);
            if (persistedArea >= 0.5) {
                return AvverkningsanmalanRaskStatus.okand;
            }

            const area = this.spatialService.getAreaWkt(atgardsgeometri);
            return area > 0.5 ? AvverkningsanmalanRaskStatus.ny : AvverkningsanmalanRaskStatus.okand;
        }

        const diff = atgardsgeometri && avverkningsanmalanGeometri ? this.spatialService.getAreaDifference(atgardsgeometri, avverkningsanmalanGeometri) : 0;
        if (diff > 0.5) {
            return AvverkningsanmalanRaskStatus.ogiltig;
        }

        return diff > 0 && diff < 0.5 ? AvverkningsanmalanRaskStatus.andrad : AvverkningsanmalanRaskStatus.okand;
    }
}
